import React from 'react';
import Modal from '@hero/ui-kit/components/Modal';
import H from '@hero/ui-kit/typography/H';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import useStickyNote from '../../api/useStickyNote';
import { LeadNotePayload } from '../../types';
import * as Style from './style.module.scss';

interface NoteModalProps {
    leadId: string;
    existingNote: { id: number; body: string } | null;
    onClose: () => void;
    // [HWM-22633 round 4] The owning table patches a local override map from
    // this payload so the row's `latest_note` / `has_notes` / orange dot update
    // without a list refetch.
    onPatchListRow?: (leadId: string, latestNote: LeadNotePayload | null) => void;
}

// Add / edit / delete the lead's sticky note from the Form Fill list without
// navigating to the detail page. Shares its save/delete routing with the detail
// page's StickyNotePanel via `useStickyNote`.
const NoteModal: React.FC<NoteModalProps> = ({ leadId, existingNote, onClose, onPatchListRow }) => {
    const { draft, setDraft, canSave, isSaving, isDeleting, isPending, save, remove } =
        useStickyNote({
            leadId,
            note: existingNote,
            onSaved: (latest) => {
                onPatchListRow?.(leadId, latest);
                onClose();
            }
        });

    return (
        <Modal isCancelable onClose={onClose} className={Style.modalSmall}>
            <div className={Style.modalBody}>
                <H role="h4" centered noDefaultMargin>
                    {existingNote ? 'Edit sticky note' : 'Add sticky note'}
                </H>

                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className={Style.stickyNoteTextarea}
                    placeholder="Add a sticky note for this form fill…"
                    autoFocus
                />

                <Section noDefaultPadding className={Style.actionsCentered}>
                    {existingNote ? (
                        <Button variant="outlineSecondary" onClick={remove} disabled={isPending}>
                            {isDeleting ? 'Deleting…' : 'Delete'}
                        </Button>
                    ) : null}
                    <Button variant="outlineSecondary" onClick={onClose} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button onClick={save} disabled={!canSave}>
                        {isSaving ? 'Saving…' : 'Save'}
                    </Button>
                </Section>
            </div>
        </Modal>
    );
};

export default NoteModal;
